export const SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP = {
  canViewAdvertisements: 'can-view-advertisements',
  canEditAdvertisements: 'can-edit-advertisements',
  canDeleteAdvertisements: 'can-delete-advertisements',
  canViewBookings: 'can-view-bookings',
  canEditBookings: 'can-edit-bookings',
  canDeleteBookings: 'can-delete-bookings',
  canViewProperties: 'can-view-properties',
  canEditProperties: 'can-edit-properties',
  canDeleteProperties: 'can-delete-properties',
  canViewPosts: 'can-view-posts',
  canEditPosts: 'can-edit-posts',
  canDeletePosts: 'can-delete-posts',
  canViewUsers: 'can-view-users',
  canEditUsers: 'can-edit-users',
  canDeleteUsers: 'can-delete-users',
  canViewChat: 'can-view-chat',
  canEditChat: 'can-edit-chat',
  canDeleteChat: 'can-delete-chat',
  canViewFiles: 'can-view-files',
  canEditFiles: 'can-edit-files',
  canDeleteFiles: 'can-delete-files',
  canViewAnalytics: 'can-view-analytics',
  canViewExports: 'can-view-exports',
  canViewOrders: 'can-view-orders',
  canViewCities: 'can-view-cities',
  canEditCities: 'can-edit-cities',
  canDeleteCities: 'can-delete-cities'
} as const

export type SidebarMenuPermissionKey = keyof typeof SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP

export type SidebarMenuCapability =
  (typeof SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP)[SidebarMenuPermissionKey]

export const SIDEBAR_MENU_PERMISSION_OPTIONS: {
  key: SidebarMenuPermissionKey
  capability: SidebarMenuCapability
  label: string
}[] = [
  {
    key: 'canViewAdvertisements',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewAdvertisements,
    label: 'Visualizza annunci'
  },
  {
    key: 'canEditAdvertisements',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditAdvertisements,
    label: 'Modifica annunci'
  },
  {
    key: 'canDeleteAdvertisements',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteAdvertisements,
    label: 'Elimina annunci'
  },
  {
    key: 'canViewBookings',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewBookings,
    label: 'Visualizza prenotazioni'
  },
  {
    key: 'canEditBookings',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditBookings,
    label: 'Modifica prenotazioni'
  },
  {
    key: 'canDeleteBookings',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteBookings,
    label: 'Elimina prenotazioni'
  },
  {
    key: 'canViewProperties',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewProperties,
    label: 'Visualizza proprietà'
  },
  {
    key: 'canEditProperties',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditProperties,
    label: 'Modifica proprietà'
  },
  {
    key: 'canDeleteProperties',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteProperties,
    label: 'Elimina proprietà'
  },
  {
    key: 'canViewPosts',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewPosts,
    label: 'Visualizza post'
  },
  {
    key: 'canEditPosts',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditPosts,
    label: 'Modifica post'
  },
  {
    key: 'canDeletePosts',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeletePosts,
    label: 'Elimina post'
  },
  {
    key: 'canViewUsers',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewUsers,
    label: 'Visualizza utenti'
  },
  {
    key: 'canEditUsers',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditUsers,
    label: 'Modifica utenti'
  },
  {
    key: 'canDeleteUsers',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteUsers,
    label: 'Elimina utenti'
  },
  {
    key: 'canViewChat',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewChat,
    label: 'Visualizza chat'
  },
  {
    key: 'canViewAnalytics',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewAnalytics,
    label: 'Visualizza analitiche'
  },
  {
    key: 'canViewExports',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewExports,
    label: 'Visualizza export'
  },
  {
    key: 'canViewOrders',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewOrders,
    label: 'Visualizza ordini'
  },
  {
    key: 'canViewCities',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewCities,
    label: 'Visualizza città'
  },
  {
    key: 'canEditCities',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditCities,
    label: 'Modifica città'
  },
  {
    key: 'canDeleteCities',
    capability: SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteCities,
    label: 'Elimina città'
  }
]

const SIDEBAR_MENU_CAPABILITIES = Object.values(
  SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP
) as SidebarMenuCapability[]

export const isSidebarMenuCapability = (value: string): value is SidebarMenuCapability =>
  SIDEBAR_MENU_CAPABILITIES.includes(value as SidebarMenuCapability)

export const getSidebarMenuCapabilities = (
  capabilities?: string[] | null
): SidebarMenuCapability[] => (capabilities ?? []).filter(isSidebarMenuCapability)

export const getSidebarMenuPermissionFlagsFromCapabilities = (capabilities?: string[] | null) => {
  const normalizedCapabilities = getSidebarMenuCapabilities(capabilities)

  return {
    canViewAdvertisements: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewAdvertisements
    ),
    canEditAdvertisements: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditAdvertisements
    ),
    canDeleteAdvertisements: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteAdvertisements
    ),
    canViewBookings: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewBookings
    ),
    canEditBookings: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditBookings
    ),
    canDeleteBookings: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteBookings
    ),
    canViewProperties: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewProperties
    ),
    canEditProperties: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditProperties
    ),
    canDeleteProperties: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteProperties
    ),
    canViewPosts: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewPosts
    ),
    canEditPosts: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditPosts
    ),
    canDeletePosts: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeletePosts
    ),
    canViewUsers: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewUsers
    ),
    canEditUsers: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditUsers
    ),
    canDeleteUsers: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteUsers
    ),
    canViewChat: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewChat
    ),
    canEditChat: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditChat
    ),
    canDeleteChat: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteChat
    ),
    canViewFiles: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewFiles
    ),
    canEditFiles: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditFiles
    ),
    canDeleteFiles: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteFiles
    ),
    canViewAnalytics: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewAnalytics
    ),
    canViewExports: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewExports
    ),
    canViewOrders: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewOrders
    ),
    canViewCities: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canViewCities
    ),
    canEditCities: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canEditCities
    ),
    canDeleteCities: normalizedCapabilities.includes(
      SIDEBAR_MENU_PERMISSION_CAPABILITY_MAP.canDeleteCities
    )
  }
}
